import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import moment from 'moment';
import { APP } from 'config';
import {
  ActionsMenu,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
} from 'melp-design/components';
import { useSearchParams } from 'utils/navigation';
import { useModalContext } from 'store/modal';
import {
  adminTermsFiltersSchema,
  TermsVersion,
  useAdminTerms,
} from 'store/admin-terms';
import { TermsVersionModal } from '../atoms';

export const AdminTermsList: FC = () => {
  const { t } = useTranslation();
  const { openModal } = useModalContext();
  const { searchParams: filters, navigate } = useSearchParams(
    adminTermsFiltersSchema,
  );
  const { data: terms, isLoading } = useAdminTerms(filters);

  const openVersion = (term?: TermsVersion) => {
    openModal({
      component: TermsVersionModal,
      props: { termId: term?.id },
      size: 'xl',
    });
  };

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <ListTotal total={terms?.total} />
          <ActionsMenu
            type="spread"
            items={[
              {
                label: t('termsAndConditions.newVersion', {
                  defaultValue: 'New version',
                }),
                color: 'primary',
                onClick: () => openVersion(),
              },
            ]}
          />
        </Stack>
      </TableToolbar>
      <Table
        data={terms?.items ?? []}
        isLoading={isLoading}
        onRowClick={(term) => openVersion(term)}
        columns={[
          {
            id: 'version',
            header: t('termsAndConditions.versionColumn', {
              defaultValue: 'Version',
            }),
            cell: ({ row: { original: term } }) => term.version,
            meta: { sort: true },
          },
          {
            id: 'creationDate',
            header: t('common.createdAt', { defaultValue: 'Created at' }),
            cell: ({ row: { original: term } }) =>
              moment(term.creationDate).format('l LT'),
            meta: { sort: true },
          },
          {
            id: 'createdByName',
            header: t('termsAndConditions.createdBy', {
              defaultValue: 'Created by',
            }),
            cell: ({ row: { original: term } }) => term.createdByName ?? '—',
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (sort) => navigate({ params: { sort } }),
        }}
        pagination={
          terms && {
            count: terms.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
